import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { defaultSettings, type SiteSettings } from "@/pages/admin/ayarlar/settings";

export interface UseSiteSettings {
  settings: SiteSettings;
  loading: boolean;
  saving: boolean;
  error: string | null;
  load: () => Promise<void>;
  save: (next: SiteSettings) => Promise<boolean>;
}

/**
 * Site geneli ayarları dataClient'deki `site_settings` tablosundan okur ve kaydeder.
 * Tek satır (key="main") içinde tüm ayar JSON olarak tutulur.
 */
export function useSiteSettings(): UseSiteSettings {
  const { role, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const requestId = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const id=++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest<{settings: Partial<SiteSettings>}>(role === "admin" ? "/admin/site-settings" : "/site/settings");
      const merged: SiteSettings = { ...defaultSettings, ...data.settings };

      if (mounted.current && id===requestId.current) setSettings(merged);
    } catch (e) {
      if (mounted.current && id===requestId.current) {
        setError(e instanceof Error ? e.message : "Ayarlar yüklenirken bir hata oluştu.");
      }
    } finally {
      if (mounted.current && id===requestId.current) setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    if (!authLoading) load();
    const requests=requestId;
    return ()=>{requests.current++;};
  }, [load, authLoading]);

  const save = useCallback(async (next: SiteSettings): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      const data=await apiRequest<{settings: SiteSettings}>("/admin/site-settings",{method:"PUT",body:{settings:next}});
      if (mounted.current) setSettings({...defaultSettings,...data.settings});
      return true;
    } catch (e) {
      if (mounted.current) {
        setError(e instanceof Error ? e.message : "Ayarlar kaydedilirken bir hata oluştu.");
      }
      return false;
    } finally {
      if (mounted.current) setSaving(false);
    }
  }, []);

  return { settings, loading, saving, error, load, save };
}
